import * as amplitude from '@amplitude/analytics-browser';
import type { Types } from '@amplitude/analytics-browser';
import ROUTES from '../constants/routes';
import { getAnonymousId } from '../utils/anonymousId';
import { logger } from '../utils/logger';

/**
 * Dueño ÚNICO de la grabación de sesiones: Amplitude (analytics + Session
 * Replay) y Microsoft Clarity. Contrato:
 *
 *  - `initSessionRecording()` corre UNA vez al boot (App). El init de Amplitude
 *    es síncrono (ningún evento se pierde) pero NO enciende el replay: la
 *    grabación es por VENTANA, no de toda la sesión.
 *  - `acquireSessionReplayWindow(reason)` / `releaseSessionReplayWindow(reason)`
 *    abren y cierran esas ventanas, con CONTEO de referencias (ver openWindows).
 *    Los componentes no las llaman directo: usan `useSessionReplayWindow`.
 *    Amplitude no expone start/stop: su mecanismo documentado es agregar y
 *    quitar el plugin (`amplitude.add` / `amplitude.remove`), que es lo que
 *    hacen por debajo. El plugin (rrweb, ~300KB) se carga la primera vez que se
 *    abre una ventana, no al boot. El sampleRate EFECTIVO lo manda la config
 *    remota de Amplitude (dashboard → Session Replay settings — tiene
 *    precedencia sobre lo local y se ajusta en vivo sin deploy cuando la cuota
 *    apriete); el valor local de abajo es solo el FALLBACK.
 *  - Clarity NO tiene start/stop. Su primitiva equivalente es `upgrade`, que
 *    PRIORIZA la sesión para que sobreviva el muestreo diario (100k/día por
 *    proyecto). Por eso start la marca en vez de intentar arrancarla.
 *  - `identifyRecordedUser()` es el ÚNICO punto de identidad de grabaciones:
 *    amarra el user_id interno en Amplitude (setUserId) y en Clarity — identify
 *    Y el custom tag `user_id`, porque identify solo NO es filtrable en el
 *    dashboard de Clarity; el tag sí. Idempotente: se puede re-llamar en cada
 *    login/merge.
 *  - `getSessionRecordingIds()` expone device/session id de Amplitude — el
 *    amarre que nexus necesita para deep-linkear al replay exacto.
 *  - El project id de Clarity vive en VITE_CLARITY_ID (index.html lo templa):
 *    dev/preview ya no graban al proyecto de prod.
 *
 * Verificación en vivo: /dev-test/session-recording (staff-gated).
 */

const DEFAULT_REPLAY_SAMPLE_RATE = 1;

/**
 * Privacidad de la grabación, declarada explícitamente en vez de heredar el
 * default del SDK: en una app fiscal se teclean RFC, CIEC y contraseña de la
 * e.firma, y eso no puede depender de qué default traiga la versión que quedó
 * instalada.
 *
 * `medium` enmascara el CONTENIDO de todos los inputs (que es donde viven esos
 * datos) conservando el layout. Para tapar además texto plano puntual (un nodo
 * que renderea RFC/nombre), el SDK respeta nativo las clases `amp-mask`
 * (enmascara el texto) y `amp-block` (bloquea el elemento) — se ponen en el
 * JSX del nodo, sin tocar esta config. (La versión instalada del plugin NO
 * soporta mask levels por URL; su privacyConfig es global.)
 */
const REPLAY_PRIVACY_CONFIG = {
	defaultMaskLevel: 'medium' as const,
	// El `value` de un `input[type=file]` es el NOMBRE del archivo, y en esta app
	// ese nombre ES el RFC del usuario (`koar960116be2.cer`,
	// `Claveprivada_FIEL_KOAR960116BE2_*.key`). `medium` enmascara el contenido de
	// los inputs de texto pero no ese value, así que la pantalla de e.firma
	// filtraba un dato personal en claro. Se enmascara explícitamente ahora que
	// esa pantalla se graba a propósito.
	maskSelector: ['input[type="file"]'],
};

/**
 * Ventanas de grabación abiertas, por `reason`. Es un CONJUNTO y no un booleano
 * porque las superficies se ANIDAN: la pantalla de compra exitosa y el
 * formulario de vinculación se montan dentro del host de onboarding, que ya
 * tiene su propia ventana abierta. Con un booleano, el desmontaje de la interior
 * cerraría la del host y perderíamos el resto de la sesión — justo el tramo que
 * interesa. La grabación se corta al soltar la ÚLTIMA ventana.
 */
const openWindows = new Set<string>();

let replayActive = false;
let replaySampleRate: number | null = null;
let identifiedUserId: string | null = null;

const clarity = (...args: unknown[]): void => {
	try {
		if (typeof window.clarity === 'function') window.clarity(...args);
	} catch (error) {
		logger.warn('clarity call failed', error);
	}
};

/**
 * Nombre con el que Amplitude registró el plugin — la llave que pide
 * `amplitude.remove` para apagarlo. Se GUARDA DE LA INSTANCIA en vez de
 * escribirse a mano, y eso no es un detalle:
 *
 * estaba hardcodeado como `'sessionReplayTracking'` y el upgrade del plugin
 * (1.23.6 → 1.33.6, 27-jul-2026) lo renombró a
 * `'@amplitude/plugin-session-replay-browser'`. `remove()` con la llave vieja no
 * falla: Amplitude escribe un warning y sigue. O sea que `stopSessionReplay()`
 * dejó de apagar nada sin que nadie se enterara — la grabación seguía corriendo
 * fuera de su ventana. Derivarlo de la instancia hace imposible que vuelva a
 * driftear en el próximo upgrade.
 */
let replayPluginName: string | null = null;

/**
 * Flag de auto-grabación para pruebas internas (staff): con
 * `localStorage.replay_force = '1'` este browser abre una ventana global al
 * boot — TODO lo que se haga queda grabado, en cualquier flujo, sin tocar a
 * los demás usuarios. Dos capas, porque abrir la ventana NO basta:
 *
 *  1. La ventana monta el plugin (capa local).
 *  2. El sampling/targeting REMOTO decide si la sesión se captura (config del
 *     dashboard; hoy 5% base). Por eso se emite el evento marcador
 *     `replay_debug_active`: un segmento de targeting al 100% condicionado a
 *     ese evento (Settings → Session Replay → Targeting) hace la captura
 *     determinista para quien tenga el flag. Sin el segmento, el flag solo
 *     mejora la suerte, no la garantiza.
 */
const REPLAY_FORCE_KEY = 'replay_force';

const isReplayForced = (): boolean => {
	try {
		return window.localStorage.getItem(REPLAY_FORCE_KEY) === '1';
	} catch {
		return false;
	}
};

/**
 * Device id de Amplitude = `heru_anon_id`, el id anónimo compartido con el sitio
 * (cookie en `.heru.app` + `?heru_anon_id=` en los links del sitio hacia la app,
 * ver anonymousId.ts). El sitio (heru-website) ya inicializa SU proyecto de
 * Amplitude con ese mismo valor como device id; al usarlo aquí también, la
 * visita en www.heru.app y la sesión en web.heru.app comparten device_id y se
 * pueden unir dentro de Amplitude (User Look-Up, cohortes, replays) y no solo
 * en el warehouse por touchpoints. Sin esto el SDK genera un UUID propio.
 *
 * Solo se pasa cuando `getAnonymousId` devolvió un id real: sus fallbacks
 * ('anonymous' / 'server') colapsarían a todos los usuarios sin cookie en un
 * mismo device; en ese caso se deja que el SDK genere el suyo.
 */
const sharedDeviceId = (): string | undefined => {
	const id = getAnonymousId();
	return id && id !== 'anonymous' && id !== 'server' ? id : undefined;
};

/**
 * Páginas donde el autocapture NO registra interacciones con elementos ni
 * frustraciones (rage/dead clicks): los dos canales que mandan a Amplitude el
 * TEXTO de lo que se toca (`[Amplitude] Element Text`, `Element Parent Label`).
 * Aquí se pintan nombre y RFC de clientes y emisores dentro de botones y filas
 * clicables, y la clase `amp-mask` solo tapa el replay, no ese texto. Ambas
 * superficies traen sus propios eventos explícitos.
 *
 *  - Facturación v2: todo lo que cuelga de `/facturacion`.
 *  - La página pública del QR: todo el flujo público vive bajo `/factura-qr`.
 *
 * Se declaran como rutas y se cubre su árbol completo; una superficie nueva es
 * una ruta más en esta lista.
 */
const AUTOCAPTURE_INTERACTIONS_EXCLUDED_ROUTES = [ROUTES.FACTURACION, ROUTES.QR_PUBLIC];

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * El SDK compara contra `window.location.href` completo. El patrón exige el
 * prefijo fijo de la ruta (lo anterior al primer `/:param`) como segmento
 * entero: cubre `/facturacion` y lo que cuelga, no `/facturacion-qr` ni un
 * `/facturacionx`. Sin distinguir mayúsculas, igual que el router.
 */
const routeSubtreePattern = (route: string) =>
	new RegExp(`^[^:/?#]+://[^/?#]+${escapeRegExp(route.split('/:')[0])}(?:[/?#]|$)`, 'i');

/** Un objeto por plugin: el de interacciones reescribe sus opciones al arrancar. */
const interactionsPrivacy = () => ({
	pageUrlExcludelist: AUTOCAPTURE_INTERACTIONS_EXCLUDED_ROUTES.map(routeSubtreePattern),
});

/**
 * Exactamente lo que enciende `autocapture: true` en el SDK instalado, más la
 * exclusión de arriba. Cada llave va declarada porque en forma de objeto las
 * omitidas NO heredan el default de `true`: `elementInteractions`,
 * `frustrationInteractions`, `networkTracking` y `webVitals` se apagan si faltan.
 * `performanceTracking` ya estaba apagado con `true`.
 *
 * La config remota de autocapture (dashboard de Amplitude) se mezcla por llave
 * de primer nivel: si algún día publica `elementInteractions` o
 * `frustrationInteractions`, REEMPLAZA estos objetos y la exclusión se pierde.
 * Al 30-sep-2026 `configs.analyticsSDK` viene vacío.
 */
const AUTOCAPTURE: Types.AutocaptureOptions = {
	attribution: true,
	sessions: true,
	pageViews: true,
	formInteractions: true,
	fileDownloads: true,
	elementInteractions: interactionsPrivacy(),
	frustrationInteractions: interactionsPrivacy(),
	networkTracking: true,
	webVitals: true,
	pageUrlEnrichment: true,
	performanceTracking: false,
};

let sessionRecordingInitialized = false;

export const initSessionRecording = (): void => {
	if (sessionRecordingInitialized) return;
	sessionRecordingInitialized = true;
	amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
		autocapture: AUTOCAPTURE,
		deviceId: sharedDeviceId(),
	});
	if (isReplayForced()) {
		acquireSessionReplayWindow('force_debug');
		logger.info('replay FORZADO por localStorage.replay_force');
	}
};

/** Abre una ventana de grabación. Idempotente: llamarla dos veces con el mismo
 * `reason` no duplica el plugin ni recarga rrweb. `reason` viaja a Clarity como
 * razón del upgrade, así que aparece como filtro en su dashboard. */
export const acquireSessionReplayWindow = (reason: string): void => {
	clarity('upgrade', reason);
	clarity('set', 'replay_window', reason);
	if (openWindows.has(reason)) return;
	openWindows.add(reason);
	logger.info('replay ventana ABRE', {
		reason,
		abiertas: [...openWindows],
	});
	if (replayActive) return;
	replayActive = true;
	const sampleRate = DEFAULT_REPLAY_SAMPLE_RATE;
	void import('@amplitude/plugin-session-replay-browser').then(
		({ sessionReplayPlugin }) => {
			// Soltaron la última ventana mientras el plugin (~300KB) bajaba: no lo
			// montes. Sin esto quedaría grabando sin dueño y ya nadie lo apagaría.
			if (!replayActive) return;
			const plugin = sessionReplayPlugin({
				sampleRate,
				privacyConfig: REPLAY_PRIVACY_CONFIG,
			});
			replayPluginName = plugin.name ?? null;
			const added = amplitude.add(plugin);
			replaySampleRate = sampleRate;
			// El marcador de debug se emite cuando el SETUP del plugin terminó
			// (add().promise), no antes: el targeting por evento se evalúa
			// client-side dentro del plugin, y su setup es async (incluye el
			// fetch de la config remota). Un track que pasa por el pipeline
			// antes de eso es invisible para la condición del 100% — verificado
			// E2E dos veces: la sesión caía al 5% base y no se capturaba.
			void added.promise.then(() => {
				if (isReplayForced()) {
					amplitude.track('replay_debug_active');
					logger.info('replay marcador debug emitido (plugin listo)');
				}
			});
			logger.info('session replay ABIERTO', {
				reason,
				sampleRate,
				...getSessionRecordingIds(),
			});
		},
	);
};

/** Suelta UNA ventana. Solo corta la grabación si era la última abierta. */
export const releaseSessionReplayWindow = (reason: string): void => {
	if (!openWindows.delete(reason)) return;
	logger.info('replay ventana CIERRA', {
		reason,
		abiertas: [...openWindows],
	});
	if (openWindows.size > 0) return;
	stopSessionReplay();
};

/** Corta la grabación pase lo que pase, soltando todas las ventanas. Es lo que
 * necesita el logout: nadie sigue grabado tras cerrar sesión, aunque una
 * superficie no haya alcanzado a desmontarse. Amplitude deja de capturar; lo ya
 * grabado se sube igual. Clarity no se puede parar: la sesión sigue, solo deja
 * de estar priorizada. */
export const stopSessionReplay = (): void => {
	openWindows.clear();
	if (!replayActive) return;
	replayActive = false;
	replaySampleRate = null;
	if (replayPluginName) {
		void amplitude.remove(replayPluginName);
		replayPluginName = null;
	}
	logger.info('session replay CERRADO', getSessionRecordingIds());
};

export const identifyRecordedUser = (userId: string | number): void => {
	const id = String(userId);
	try {
		amplitude.setUserId(id);
	} catch (error) {
		logger.error('amplitude setUserId failed', error);
	}
	clarity('identify', id);
	clarity('set', 'user_id', id);
	identifiedUserId = id;
};

/**
 * Corta la identidad de grabaciones al cerrar sesión. `amplitude.reset()` borra
 * el userId y genera un deviceId NUEVO.
 *
 * Por qué importa: Amplitude fusiona identidades POR DEVICE. Sin este reset, el
 * navegador conserva el mismo deviceId entre cuentas, así que al salir A y
 * entrar B, Amplitude puede colapsar a los dos en un mismo usuario y las
 * grabaciones de B quedan colgadas de un device que también es de A. Es la misma
 * clase de fuga que `clearSession` ya arregla para el caché HTTP y de React
 * Query — esta es la que faltaba, la de analytics.
 *
 * También cierra la ventana de grabación: quien cierra sesión deja de grabarse
 * en el acto, no hasta la siguiente carga.
 */
export const resetRecordedUser = (): void => {
	stopSessionReplay();
	try {
		amplitude.reset();
	} catch (error) {
		logger.error('amplitude reset failed', error);
	}
	identifiedUserId = null;
};

export const getSessionRecordingIds = (): {
	ampDeviceId: string | null;
	ampSessionId: number | null;
} => ({
	ampDeviceId: amplitude.getDeviceId() ?? null,
	ampSessionId: amplitude.getSessionId() ?? null,
});

/** Estado introspectable para la página de verificación de /dev-test. */
export const getSessionRecordingStatus = () => ({
	amplitudeKeyPresent: Boolean(import.meta.env.VITE_AMPLITUDE_API_KEY),
	replayActive,
	replaySampleRate,
	openWindows: [...openWindows],
	clarityLoaded: typeof window.clarity === 'function',
	clarityProjectId: (import.meta.env.VITE_CLARITY_ID as string) || null,
	identifiedUserId,
	replayForced: isReplayForced(),
	...getSessionRecordingIds(),
});
